import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { FaSearch } from 'react-icons/fa';
import { FaWandMagicSparkles } from 'react-icons/fa6';
import './SearchInput.css';

const DEFAULT_PLACEHOLDERS = [
  'Recent college grad building in agentic AI, pre-seed',
  'First-time founder with 5 years robotics experience, raising pre-seed',
  'Ex-Stripe engineer, 3 years payments experience, building fintech',
  'Healthcare founder, former nurse, bootstrapping telehealth startup',
  'AI researcher, PhD dropout, building developer tools',
];

const SearchInput = forwardRef(({
  // Core props
  query,
  onQueryChange,
  onSubmit,
  isExpanded = false,
  isExpanding = false,
  isSubmitting = false,
  disabled = false,

  // Customization
  placeholderExamples = DEFAULT_PLACEHOLDERS,
  expandedButtonText = 'Start search',
  hintTextDefault = <>Hint: Specify <strong>location</strong>, <strong>funding stage</strong>, <strong>founder background</strong>, and <strong>product market</strong></>,
  hintTextExpanded = "Your search query has been optimized for our AI-search. You'll get the best results if you're specific and provide lots of details. Feel free to re-edit the query before you fire off the search.",

  // Optional template support
  selectedTemplate = null,
  expandedTemplateText = null,
  templates = [],
  onTemplateClick,
  onCreateTemplate,

  // Optional edit button (for HomePage)
  showEditButton = false,
  onEditClick,

  // Highlighting (controlled by parent after expansion)
  isHighlighting = false,
  formattedQuery = null, // Query with [[markers]] for highlighting
  onHighlightingStop, // Callback to stop highlighting when user edits
}, ref) => {
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isPlaceholderFading, setIsPlaceholderFading] = useState(false);
  const [isMultiline, setIsMultiline] = useState(false);
  const textareaRef = useRef(null);

  // Expose textarea ref to parent
  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    getTextarea: () => textareaRef.current,
  }));

  // Animate placeholder text
  useEffect(() => {
    const interval = setInterval(() => {
      setIsPlaceholderFading(true);

      setTimeout(() => {
        setPlaceholderIndex((prev) => (prev + 1) % placeholderExamples.length);
        setIsPlaceholderFading(false);
      }, 500);
    }, 3500);

    return () => clearInterval(interval);
  }, [placeholderExamples.length]);

  // Auto-resize textarea and detect multiline
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${scrollHeight}px`;

      // Check if content is multiline
      setIsMultiline(scrollHeight > 52 || selectedTemplate !== null || expandedTemplateText !== null);
    }
  }, [query, selectedTemplate, expandedTemplateText]);

  const hasContent = query.trim() || selectedTemplate;

  // Parse formatted query with [[markers]] and render with highlights
  const renderHighlightedText = () => {
    const textToParse = formattedQuery || query;
    if (!textToParse) return null;

    const parts = [];
    let lastIndex = 0;
    const regex = /\[\[(.*?)\]\]/g;
    let match;

    while ((match = regex.exec(textToParse)) !== null) {
      // Add text before the match (unhighlighted)
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`} className="search-highlight-normal">
            {textToParse.slice(lastIndex, match.index)}
          </span>
        );
      }
      // Add the matched text (highlighted)
      parts.push(
        <span key={`highlight-${match.index}`} className="search-highlight-text">
          {match[1]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add any remaining text after the last match
    if (lastIndex < textToParse.length) {
      parts.push(
        <span key={`text-${lastIndex}`} className="search-highlight-normal">
          {textToParse.slice(lastIndex)}
        </span>
      );
    }

    return parts.length > 0 ? parts : <span className="search-highlight-normal">{textToParse}</span>;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      alert('Search is not available at this time. Please try again later.');
    }
  };

  const handleChange = (e) => {
    // Stop highlighting immediately when user starts editing
    if (isHighlighting && onHighlightingStop) {
      onHighlightingStop();
    }
    onQueryChange(e.target.value);
  };

  const hasTemplateDisplay = selectedTemplate || expandedTemplateText;

  return (
    <div className="search-input-container">
      <div className={`search-input-wrapper ${isHighlighting ? 'is-highlighting' : ''}`}>
        <div className={`search-highlight-overlay ${isMultiline ? 'multiline' : ''}`}>
          {renderHighlightedText()}
        </div>

        {hasTemplateDisplay ? (
          <div className={`search-input-field-container has-template ${isMultiline ? 'multiline' : ''}`}>
            <textarea
              ref={textareaRef}
              value={query}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={isExpanded ? '' : 'Add additional criteria...'}
              className={`search-input with-template ${isMultiline ? 'multiline' : ''} ${isExpanded ? 'expanded' : ''}`}
              disabled={disabled || isSubmitting}
              rows={1}
            />
            <span className="search-template-prefix">{expandedTemplateText || selectedTemplate?.text}</span>
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholderExamples[placeholderIndex]}
            className={`search-input ${isPlaceholderFading ? 'placeholder-fade' : ''} ${isExpanded ? 'expanded' : ''} ${isMultiline ? 'multiline' : ''}`}
            disabled={disabled || isSubmitting}
            rows={1}
          />
        )}

        {showEditButton ? (
          <button
            type="button"
            className="search-edit-button"
            onClick={onEditClick}
            disabled={isSubmitting}
          >
            Edit
          </button>
        ) : (
          <button
            type="submit"
            className={`search-go-button ${isExpanded ? 'expanded' : ''} ${isExpanding ? 'expanding' : ''} ${isMultiline ? 'multiline' : ''}`}
            disabled={isSubmitting || isExpanding || !hasContent}
            onClick={() => alert('Search is not available at this time. Please try again later.')}
          >
            {isExpanding ? <><span className="search-button-expanding-text">Enriching</span><div className="search-button-loader"></div></> : isExpanded ? <><FaSearch className="search-button-icon" />{expandedButtonText}</> : <><FaWandMagicSparkles className="search-button-icon" /><span className="search-button-text">Enrich query</span></>}
          </button>
        )}
      </div>

      <p className="search-hint">
        {isExpanded ? hintTextExpanded : hintTextDefault}
      </p>

      {/* Template pills - only render if templates are provided */}
      {(templates.length > 0 || onCreateTemplate) && (
        <div className="search-templates">
          <span className="search-templates-label">Templates:</span>
          {templates.length > 0 ? (
            templates.map((template) => (
              <button
                key={template.id}
                type="button"
                className={`search-template-pill ${selectedTemplate?.id === template.id ? 'selected' : ''} ${isExpanded ? 'disabled' : ''}`}
                onClick={() => onTemplateClick?.(template)}
                disabled={isExpanded}
              >
                {template.name}
              </button>
            ))
          ) : onCreateTemplate ? (
            <button
              type="button"
              className="search-template-pill create-template"
              onClick={onCreateTemplate}
            >
              + Create reusable template
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;

